import classNames from "classnames";
import * as React from "react";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import ReactModal from "react-modal";

import style from "./modal.scss";
import NotificationModal from "./NotificationModal";
import videoModalStyle from "./video-modal/video-modal.scss";
import ErrorMessage from "components/error-message/ErrorMessage";
import Exit from "components/icons/Exit";
import OpenInNewTab from "components/icons/OpenInNewTab";
import Tooltip from "components/tooltip/Tooltip";
import Heading from "components/typography/heading/Heading";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Props {
    isOpen: boolean;
    hideModal: () => void;
    modalTitle?: string;
    modalDescription?: string;
    children: React.ReactNode;
    action?: React.ReactNode;
    overlayBackgroundImage?: boolean;
    closeButton?: boolean;
    openInNewTab?: () => void;
    titleIcon?: React.ReactNode;
    maxHeight?: number;
    height?: string;
    width?: string;
    isVideoPopup?: boolean;
    className?: string;
}

const Modal = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const setBodyScrolling = (currentScrollingState: boolean) => {
        const body = document.body;
        body.classList.remove(currentScrollingState ? style.bodyStopScroll : style.bodyScroll);
        body.classList.add(currentScrollingState ? style.bodyScroll : style.bodyStopScroll);
    };

    const modalTitleStyles = classNames({
        [style.title]: props.modalTitle && !props.modalDescription,
        [videoModalStyle.videoModalTitle]: props.isVideoPopup && props.modalTitle,
    });

    const modalHeaderStyles = classNames({
        [style.title]: props.modalDescription,
    });

    useEffect(() => {
        if (props.isOpen) {
            setBodyScrolling(false);
        } else {
            setBodyScrolling(true);
        }
        return function cleanup() {
            setBodyScrolling(true);
        };
    }, [props.isOpen]);

    return (
        <ErrorBoundary
            fallback={
                <NotificationModal
                    open={props.isOpen}
                    hide={props.hideModal}
                    title={t("Common.error")}
                    message={<ErrorMessage />}
                />
            }
        >
            <ReactModal
                ariaHideApp={false}
                isOpen={props.isOpen}
                onRequestClose={props.hideModal}
                className={{
                    base: classNames(style.modalBase, props.className),
                    afterOpen: style.modalAfterOpen,
                    beforeClose: style.modalBeforeClose,
                }}
                overlayClassName={{
                    base: props.overlayBackgroundImage ? style.overlayBackgroundImage : style.overlay,
                    afterOpen: "",
                    beforeClose: style.overlayBeforeClose,
                }}
                shouldCloseOnOverlayClick={!!props.isVideoPopup}
                closeTimeoutMS={parseInt(style.animationDuration, 10)}
                style={{
                    content: {
                        ...(props.width ? { width: props.width } : {}),
                    },
                }}
            >
                <div
                    className={style.modalContent}
                    style={{
                        ...(props.maxHeight ? { maxHeight: props.maxHeight } : {}),
                        ...(props.height ? { height: props.height } : {}),
                    }}
                >
                    <div className={props.modalDescription ? modalHeaderStyles : modalTitleStyles}>
                        {props.titleIcon}
                        {props.modalTitle && <Heading tag="h2">{props.modalTitle}</Heading>}
                        {props.action}
                        {props.modalDescription && (
                            <div className={style.descriptionStyle}>{props.modalDescription}</div>
                        )}
                    </div>
                    {props.children}
                </div>
                {props.closeButton ? (
                    <div className={style.closeButtonContainerVisibility} />
                ) : (
                    <div className={style.buttonContainer}>
                        {props.openInNewTab && (
                            <Tooltip content={t("Common.openInNewTab")}>
                                <button
                                    onClick={props.openInNewTab}
                                    className={style.button}
                                    data-testid={testIds.common.dialog.openInNewTabButton}
                                >
                                    <OpenInNewTab size={20} />
                                </button>
                            </Tooltip>
                        )}
                        <Tooltip content={t("Common.close")}>
                            <button
                                role="close"
                                onClick={props.hideModal}
                                className={style.button}
                                data-testid={testIds.common.dialog.xButton}
                            >
                                <Exit color={theme.iconFillColor} />
                            </button>
                        </Tooltip>
                    </div>
                )}
            </ReactModal>
        </ErrorBoundary>
    );
};

export default Modal;
